import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
/* A small note under the cart icon for an add: never blocks the page, the same note is reused on rapid adds. */
const sxAr = document.documentElement.lang.startsWith('ar');
let sxBox, sxTimer, sxBase = null;
function sxHide() {
  sxBox?.classList.remove('is-on');
  sxBase = null;
}
function sxNote(img, name, line, isError) {
  if (!sxBox) {
    sxBox = document.createElement('div');
    sxBox.className = 'sx-add';
    sxBox.setAttribute('role', 'status');
    sxBox.innerHTML = '<button type="button"><img alt="" width="44" height="44"><span><b></b><small></small></span></button>';
    sxBox.firstChild.addEventListener('click', () => {
      sxHide();
      document.getElementById('cart-drawer')?.open();
    });
    document.body.append(sxBox);
  }
  const icon = document.querySelector('.header-actions__cart-icon')?.getBoundingClientRect();
  sxBox.style.top = `${Math.max(8, (icon?.bottom ?? 0) + 6)}px`;
  if (img) sxBox.querySelector('img').src = img;
  if (name) sxBox.querySelector('b').textContent = name;
  sxBox.querySelector('small').textContent = line;
  sxBox.classList.toggle('is-err', !!isError);
  sxBox.classList.add('is-on');
  clearTimeout(sxTimer);
  sxTimer = setTimeout(sxHide, isError ? 4500 : 2800);
}
document.addEventListener('click', (e) => {
  const add = e.target.closest?.('button[name="add"].add-to-cart-button:not([data-sx-buy]):not([data-puppet="true"])');
  const form = add?.closest('form');
  if (!form || add.disabled || add.closest('dialog') || document.querySelector('.cart-drawer__inner')?.closest('dialog')?.open) return;
  const box = add.closest('add-to-cart-component');
  const title = add.closest('quick-add-component')?.dataset.productTitle || document.querySelector('h1')?.textContent.trim();
  const qty = Number(form.querySelector('input[name="quantity"]')?.value) || 1;
  // a picture the page has already loaded, so the note is whole from its first frame
  const shown = /** @type {HTMLImageElement | null} */ (document.querySelector('.sx-offer.is-on .sx-offer__pics img'));
  sxNote(shown?.currentSrc || box?.dataset.productVariantMedia, title, qty > 1 ? `✓ × ${qty}` : sxAr ? '✓ تمت الإضافة' : '✓ Added');
}, true);
/** What the hidden drawer's own rows say is in the cart, before this add's answer changes them. */
function sxLines() {
  const had = new Map();
  for (const row of document.querySelectorAll('#cart-drawer .cart-items__table-row[data-key]')) {
    const id = row.dataset.key.split(':')[0];
    had.set(id, (had.get(id) ?? 0) + Number(row.querySelector('input[type="number"]')?.value ?? 0));
  }
  return had;
}
function sxAdded(event) {
  const failed = sxAr ? 'لم تتم الإضافة. قد تكون الكمية غير متوفرة.' : "Couldn't add this. The quantity may not be available.";
  const base = (sxBase ??= sxLines());
  event.promise
    ?.then(({ detail }) => {
      // the burst so far against the cart before it: never more than what really went in
      const seen = new Map();
      for (const i of detail?.items ?? []) {
        const id = String(i.variant_id);
        const prev = seen.get(id) ?? { item: i, n: 0 };
        prev.n += i.quantity;
        seen.set(id, prev);
      }
      const added = [...seen.entries()].map(([id, x]) => ({ item: x.item, n: x.n - (base.get(id) ?? 0) })).filter((x) => x.n > 0);
      if (!added.length) return sxNote(null, null, failed, true);
      const products = new Set(added.map((x) => x.item.product_id));
      const count = added.reduce((a, x) => a + x.n, 0);
      const first = added[added.length - 1].item;
      let line;
      if (products.size > 1) {
        line = sxAr ? `أضيفت ${count} قطع من ${products.size} منتجات` : `${count} pieces from ${products.size} products`;
      } else if (added.some((x) => x.item.variant_title)) {
        line = added.map((x) => (x.n > 1 ? `${x.item.variant_title} × ${x.n}` : x.item.variant_title)).join(sxAr ? '، ' : ', ');
      } else {
        line = count > 1 ? `× ${count}` : sxAr ? 'تمت الإضافة' : 'Added';
      }
      if (detail?.didError) line += sxAr ? ' · لم تُضف البقية' : ' · the rest was not added';
      sxNote(first.image ? `${first.image}${first.image.includes('?') ? '&' : '?'}width=96` : null, first.product_title, `✓ ${line}`);
    })
    .catch(() => sxNote(null, null, failed, true));
}

class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.addEventListener('click', this.#handleInstallmentsClick, true);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.removeEventListener('click', this.#handleInstallmentsClick, true);
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /** Handles the theme-drawer opening by updating its sticky state. */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
  };

  /**
   * Closes the cart drawer before the installments dialog opens.
   *
   * The payment-terms element replaces its shadow content when cart totals change,
   * so listen on this stable ancestor instead of binding to a particular CTA instance.
   *
   * @param {MouseEvent} event
   */
  #handleInstallmentsClick = (event) => {
    const path = event.composedPath();
    const ctaIndex = path.findIndex(
      (element) => element instanceof Element && element.id === 'shopify-installments-cta'
    );
    if (ctaIndex === -1) return;

    const ctaRoot = path.slice(ctaIndex + 1).find((element) => element instanceof ShadowRoot);
    if (!ctaRoot?.host.matches('shopify-payment-terms')) return;

    this.#themeDrawer?.close();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    // Buy now needs the drawer, its step opens inside it. Any other add stays on the page with a small note.
    const buying = window.__sxBuying === true && event.action === 'add';
    if (buying) window.__sxBuying = false;
    const shouldAutoOpen = buying && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // [sx-pending] hides the old numbers until the new section arrives.
    if (buying && !sourceModal) this.#dialog?.setAttribute('sx-pending', '');
    if (shouldAutoOpen && !sourceModal) {
      this.#themeDrawer?.open();
    }
    if (event.action === 'add' && !buying && !this.#themeDrawer?.isOpen) sxAdded(event);

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());
        if (buying && detail?.didError) {
          this.#themeDrawer?.close();
          sxNote(null, null, sxAr ? 'لم تتم الإضافة. قد تكون الكمية غير متوفرة.' : "Couldn't add this. The quantity may not be available.", true);
        }

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          // already opened at the tap; if the customer closed it since, it stays closed
          settle();
        }
      })
      .catch((error) => {
        this.#dialog?.removeAttribute('sx-pending');
        if (buying) this.#themeDrawer?.close();
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
